import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, MessageSquare, Calendar } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    projectType: '',
    budget: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Call Us",
      details: "+1 (555) 123-4567",
      subtitle: "Mon-Fri 9AM-6PM PST",
      action: "tel:+15551234567"
    },
    {
      icon: Mail,
      title: "Email Us",
      details: "info@resincraftstudio.com",
      subtitle: "We respond within 24 hours",
      action: "mailto:info@resincraftstudio.com"
    },
    {
      icon: MapPin,
      title: "Visit Us",
      details: "123 Craftsman Ave",
      subtitle: "Design District, CA 90210",
      action: "https://maps.google.com"
    },
    {
      icon: Clock,
      title: "Schedule",
      details: "Free Consultation",
      subtitle: "Book a design meeting",
      action: "#"
    }
  ];

  const projectTypes = [
    'Dining Table',
    'Coffee Table',
    'Console Table',
    'Bar Top',
    'Office Furniture',
    'Custom Design',
    'Other'
  ];

  const budgetRanges = [
    'Under $1,000',
    '$1,000 - $2,500',
    '$2,500 - $5,000',
    '$5,000 - $10,000',
    'Over $10,000'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        projectType: '',
        budget: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-dark-900 to-dark-950 relative overflow-hidden">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold font-serif text-white mb-6">
              Let's Create <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Together</span>
            </h1>
            <p className="text-xl text-dark-200 max-w-3xl mx-auto leading-relaxed">
              Ready to bring your vision to life? Get in touch with our team to discuss your custom furniture project.
            </p>
          </motion.div>
        </div>
        
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent-600/10 rounded-full blur-3xl"></div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            ref={ref}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
          >
            {contactInfo.map((info, index) => (
              <motion.a
                key={index}
                href={info.action}
                variants={itemVariants}
                className="group bg-dark-800/50 backdrop-blur-md rounded-2xl p-6 border border-dark-700 hover:border-accent-500/30 transition-all duration-300 hover:scale-105"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-accent-500 to-accent-600 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <info.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {info.title}
                  </h3>
                  <div className="text-accent-400 font-medium mb-1">
                    {info.details}
                  </div>
                  <div className="text-dark-400 text-sm">
                    {info.subtitle}
                  </div>
                </div>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Form and Map */}
      <section className="py-16 bg-dark-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="bg-dark-900/50 backdrop-blur-md rounded-3xl p-8 border border-dark-700">
                <h2 className="text-3xl font-bold font-serif text-white mb-6">
                  Start Your <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Project</span>
                </h2>
                
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white font-medium mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none transition-colors duration-300"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-white font-medium mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none transition-colors duration-300"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white font-medium mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none transition-colors duration-300"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div>
                        <label className="block text-white font-medium mb-2">
                          Project Type
                        </label>
                        <select
                          name="projectType"
                          value={formData.projectType}
                          onChange={handleInputChange}
                          className="w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white focus:border-accent-500 focus:outline-none transition-colors duration-300"
                        >
                          <option value="">Select project type</option>
                          {projectTypes.map((type, index) => (
                            <option key={index} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white font-medium mb-2">
                          Budget Range
                        </label>
                        <select
                          name="budget"
                          value={formData.budget}
                          onChange={handleInputChange}
                          className="w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white focus:border-accent-500 focus:outline-none transition-colors duration-300"
                        >
                          <option value="">Select budget range</option>
                          {budgetRanges.map((range, index) => (
                            <option key={index} value={range}>{range}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-white font-medium mb-2">
                          Subject
                        </label>
                        <input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          className="w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none transition-colors duration-300"
                          placeholder="Project subject"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white font-medium mb-2">
                        Project Details *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        className="w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-dark-400 focus:border-accent-500 focus:outline-none transition-colors duration-300 resize-none"
                        placeholder="Tell us about your vision, dimensions, style preferences, timeline, and any special requirements..."
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-accent-500 to-accent-600 text-white py-4 px-8 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300 flex items-center justify-center space-x-2 group"
                    >
                      <Send className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                      <span>Send Message</span>
                    </button>
                  </form>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="text-center py-12"
                  >
                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
                    <h3 className="text-2xl font-bold text-white mb-4">
                      Message Sent Successfully!
                    </h3>
                    <p className="text-dark-300 mb-6">
                      Thank you for reaching out. We'll get back to you within 24 hours to discuss your project.
                    </p>
                    <div className="text-accent-400 font-medium">
                      Expect a response soon!
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>

            {/* Map and Additional Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Map Placeholder */}
              <div className="bg-dark-900/50 backdrop-blur-md rounded-3xl p-8 border border-dark-700 h-96 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="h-16 w-16 text-accent-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Visit Our Studio
                  </h3>
                  <p className="text-dark-300 mb-4">
                    123 Craftsman Ave<br />
                    Design District, CA 90210
                  </p>
                  <button className="text-accent-400 hover:text-accent-300 transition-colors duration-300">
                    Get Directions
                  </button>
                </div>
              </div>

              {/* FAQ Section */}
              <div className="bg-dark-900/50 backdrop-blur-md rounded-3xl p-8 border border-dark-700">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                  <MessageSquare className="h-6 w-6 text-accent-400 mr-3" />
                  Quick Questions
                </h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-white font-medium mb-2">How long does a custom piece take?</h4>
                    <p className="text-dark-300 text-sm">Most custom pieces take 4-8 weeks from design approval to completion.</p>
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-2">Do you ship nationwide?</h4>
                    <p className="text-dark-300 text-sm">Yes, we offer white-glove delivery across the United States.</p>
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-2">Can I see samples first?</h4>
                    <p className="text-dark-300 text-sm">Absolutely! We provide wood and resin samples during consultation.</p>
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-2">What's your warranty policy?</h4>
                    <p className="text-dark-300 text-sm">All pieces come with a comprehensive 3-5 year warranty.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-accent-500/20 to-accent-600/20 rounded-3xl p-12 text-center border border-accent-500/30"
          >
            <Calendar className="h-16 w-16 text-accent-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-dark-200 mb-8 max-w-2xl mx-auto">
              Schedule a free consultation to discuss your vision and get a personalized quote.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-accent-500 to-accent-600 text-white py-4 px-8 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300">
                Schedule Consultation
              </button>
              <button className="border border-accent-500 text-accent-400 py-4 px-8 rounded-lg font-medium hover:bg-accent-500/10 transition-all duration-300">
                Browse Portfolio
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;